import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_MY_CARDS_ESSENCE_TYPE } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { shardsIconUrl } from '@sorare/core/src/lib/rewards';

import { MyCardsRaritySwitcherBox } from '../MyCardsRaritySwitcherBox';
import type { EssenceRaritySwitcher_cardShardsChest } from './__generated__/index.graphql';

type BoxType = Nullable<
  Exclude<Rarity, typeof Rarity.custom_series> | 'rivals' | 'loyalty'
>;

type Props = {
  rarities: Rarity[];
  cardShardsChests: Nullable<EssenceRaritySwitcher_cardShardsChest[]>;
  type: BoxType;
  disabled?: boolean;
};

export const EssenceRaritySwitcher = ({
  cardShardsChests,
  rarities,
  type,
  disabled,
}: Props) => {
  const { generateSportContextPath } = useSportContext();
  const isLaptopAndAbove = useIsLaptop();

  if (!isLaptopAndAbove) {
    return (
      <TabBar>
        {rarities.map(key => {
          const shardsChest = cardShardsChests?.find(
            chest => chest.rarity === key
          );
          return (
            <Tab
              key={key}
              to={generateSportContextPath(ANY_SPORT_MY_CARDS_ESSENCE_TYPE, {
                params: {
                  type: key,
                },
              })}
              disabled={disabled}
            >
              <img src={shardsIconUrl(key)} alt="" height={16} />
              <LabelL bold>{shardsChest?.cardShardsCount || 0}</LabelL>
            </Tab>
          );
        })}
      </TabBar>
    );
  }

  return rarities.map(key => {
    const shardsChest = cardShardsChests?.find(chest => chest.rarity === key);
    return (
      <MyCardsRaritySwitcherBox
        key={key}
        to={generateSportContextPath(ANY_SPORT_MY_CARDS_ESSENCE_TYPE, {
          params: {
            type: key,
          },
        })}
        className={classNames({
          active: type === key,
          disabled,
        })}
      >
        <img src={shardsIconUrl(key)} alt="" width={20} height={32} />
        <LabelM bold>{shardsChest?.cardShardsCount || 0}</LabelM>
      </MyCardsRaritySwitcherBox>
    );
  });
};

EssenceRaritySwitcher.fragments = {
  cardShardsChest: gql`
    fragment EssenceRaritySwitcher_cardShardsChest on CardShardsChest {
      id
      rarity
      cardShardsCount
      newThresholdUnlocked
    }
  ` as TypedDocumentNode<EssenceRaritySwitcher_cardShardsChest>,
};
